import React, { PureComponent, cloneElement, Children } from 'react';
import { node } from 'prop-types';
import { getLocalStorage } from 'utils';
import { Provider } from './LocalStorageContext';

export default class LocalStorageProvider extends PureComponent {
    static propTypes = {
        children: node.isRequired,
    };

    constructor(props) {
        super(props);
        this.localStorage = getLocalStorage();
    }

    get = (storageKey, fetch, expiry) =>
        new Promise(resolve => {
            try {
                const cachedData = JSON.parse(
                    this.localStorage.getItem(storageKey),
                );
                const lastTimeLoad = cachedData.timestamp || 0;
                if (
                    (expiry === 0 || lastTimeLoad + expiry > Date.now()) &&
                    cachedData.data
                ) {
                    return resolve(cachedData.data);
                }
            } catch (e) {
                // we don't need to do anything
            }

            if (fetch && typeof fetch === 'function') {
                return fetch().then(res => {
                    if (res) {
                        this.set(storageKey, res);
                        resolve(res);
                    }
                    resolve([]);
                });
            }

            return resolve([]);
        });

    set = (storageKey, toSave) => {
        try {
            this.localStorage.setItem(
                storageKey,
                JSON.stringify({ timestamp: Date.now(), data: toSave }),
            );
        } catch (e) {
            // If there is no localstorage we don’t need to do anything (no history)
        }
    };

    /**
     * @param  { string } storageKey - key of local storage
     * @param  { function } fetch - function returning a promise that resolves with the data
     * @param  { number } expiry - time of cache life in ms
     * @return { function } set and get cache
     */
    createActions = (storageKey, fetch, expiry = 0) => ({
        localStorage: {
            set: this.set.bind(this, storageKey),
            get: this.get.bind(this, storageKey, fetch, expiry),
        },
    });

    render() {
        return (
            <Provider
                value={{
                    actions: {
                        set: this.set,
                        get: this.get,
                    },
                    createActions: this.createActions,
                }}
            >
                {Children.map(this.props.children, child =>
                    cloneElement(child, { ...this.props }),
                )}
            </Provider>
        );
    }
}
